import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BookOpen, Video, CheckSquare, ChevronLeft, Plus, Edit, Save, Trash2, X,
  PlayCircle, FileText, AlertTriangle, CheckCircle, Clock, Printer,
  ChevronDown, ChevronRight, ExternalLink, GripVertical, MessageSquare,
  ArrowUp, ArrowDown, StickyNote
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { COMPANY } from '../config/company';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function Resources() {
  const { section } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // State for data from API
  const [manualSections, setManualSections] = useState([]);
  const [videos, setVideos] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [adminNotes, setAdminNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Modal states
  const [showManualModal, setShowManualModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Form states
  const [manualForm, setManualForm] = useState({ title: '', title_es: '', content: '', content_es: '', order: 0 });
  const [videoForm, setVideoForm] = useState({ title: '', title_es: '', description: '', description_es: '', video_url: '', duration: '', category: 'software', order: 0 });
  const [checklistForm, setChecklistForm] = useState({ title: '', title_es: '', description: '', description_es: '', items: [], order: 0 });
  const [noteForm, setNoteForm] = useState({ resource_type: 'general', resource_id: '', note: '' });
  const [newChecklistItem, setNewChecklistItem] = useState({ text: '', text_es: '' });

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [manualRes, videoRes, checklistRes, notesRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/resources/manual`),
        fetch(`${BACKEND_URL}/api/resources/videos`),
        fetch(`${BACKEND_URL}/api/resources/checklists`),
        fetch(`${BACKEND_URL}/api/resources/notes`)
      ]);
      
      let manualData = [];
      let videoData = [];
      let checklistData = [];
      let notesData = [];
      
      if (manualRes.ok) {
        manualData = await manualRes.json();
        setManualSections(manualData);
      }
      if (videoRes.ok) {
        videoData = await videoRes.json();
        setVideos(videoData);
      }
      if (checklistRes.ok) {
        checklistData = await checklistRes.json();
        setChecklists(checklistData);
      }
      if (notesRes.ok) {
        notesData = await notesRes.json();
        setAdminNotes(notesData);
      }
      
      // If no data exists, seed defaults
      if (manualData.length === 0 && videoData.length === 0 && checklistData.length === 0) {
        await fetch(`${BACKEND_URL}/api/resources/seed`, { method: 'POST' });
        // Refetch after seeding (but only once)
        const [m, v, c] = await Promise.all([
          fetch(`${BACKEND_URL}/api/resources/manual`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/resources/videos`).then(r => r.json()),
          fetch(`${BACKEND_URL}/api/resources/checklists`).then(r => r.json())
        ]);
        setManualSections(m);
        setVideos(v);
        setChecklists(c);
      }
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  // Seed default data
  const seedDefaultData = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/resources/seed`, { method: 'POST' });
      const data = await res.json();
      toast.success(data.message);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to seed data');
    }
  };

  // MANUAL CRUD
  const saveManualSection = async () => {
    try {
      const url = editingItem 
        ? `${BACKEND_URL}/api/resources/manual/${editingItem.id}`
        : `${BACKEND_URL}/api/resources/manual`;
      const method = editingItem ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(manualForm)
      });
      
      if (res.ok) {
        toast.success(editingItem ? 'Section updated' : 'Section created');
        setShowManualModal(false);
        setEditingItem(null);
        setManualForm({ title: '', title_es: '', content: '', content_es: '', order: 0 });
        fetchAllData();
      } else {
        toast.error('Failed to save section');
      }
    } catch (error) {
      toast.error('Failed to save section');
    }
  };

  const deleteManualSection = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta sección?' : 'Delete this section?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/resources/manual/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Section deleted');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // VIDEO CRUD
  const saveVideo = async () => {
    try {
      const url = editingItem 
        ? `${BACKEND_URL}/api/resources/videos/${editingItem.id}`
        : `${BACKEND_URL}/api/resources/videos`;
      const method = editingItem ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(videoForm)
      });
      
      if (res.ok) {
        toast.success(editingItem ? 'Video updated' : 'Video created');
        setShowVideoModal(false);
        setEditingItem(null);
        setVideoForm({ title: '', title_es: '', description: '', description_es: '', video_url: '', duration: '', category: 'software', order: 0 });
        fetchAllData();
      } else {
        toast.error('Failed to save video');
      }
    } catch (error) {
      toast.error('Failed to save video');
    }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar este video?' : 'Delete this video?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/resources/videos/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Video deleted');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // CHECKLIST CRUD
  const saveChecklist = async () => {
    try {
      const url = editingItem 
        ? `${BACKEND_URL}/api/resources/checklists/${editingItem.id}`
        : `${BACKEND_URL}/api/resources/checklists`;
      const method = editingItem ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checklistForm)
      });
      
      if (res.ok) {
        toast.success(editingItem ? 'Checklist updated' : 'Checklist created');
        setShowChecklistModal(false);
        setEditingItem(null);
        setChecklistForm({ title: '', title_es: '', description: '', description_es: '', items: [], order: 0 });
        fetchAllData();
      } else {
        toast.error('Failed to save checklist');
      }
    } catch (error) {
      toast.error('Failed to save checklist');
    }
  };

  const deleteChecklist = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta lista?' : 'Delete this checklist?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/resources/checklists/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Checklist deleted');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.text.trim()) return;
    setChecklistForm({
      ...checklistForm,
      items: [...checklistForm.items, { ...newChecklistItem }]
    });
    setNewChecklistItem({ text: '', text_es: '' });
  };

  const removeChecklistItem = (index) => {
    setChecklistForm({
      ...checklistForm,
      items: checklistForm.items.filter((_, i) => i !== index)
    });
  };

  // NOTES CRUD
  const saveNote = async () => {
    try {
      const url = editingItem 
        ? `${BACKEND_URL}/api/resources/notes/${editingItem.id}`
        : `${BACKEND_URL}/api/resources/notes`;
      const method = editingItem ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(noteForm)
      });
      
      if (res.ok) {
        toast.success(editingItem ? 'Note updated' : 'Note created');
        setShowNoteModal(false);
        setEditingItem(null);
        setNoteForm({ resource_type: 'general', resource_id: '', note: '' });
        fetchAllData();
      } else {
        toast.error('Failed to save note');
      }
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm(language === 'es' ? '¿Eliminar esta nota?' : 'Delete this note?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/resources/notes/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Note deleted');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  // Open edit modals with data
  const openEditManual = (item) => {
    setEditingItem(item);
    setManualForm({ 
      title: item.title || '', 
      title_es: item.title_es || '', 
      content: item.content || '', 
      content_es: item.content_es || '', 
      order: item.order || 0 
    });
    setShowManualModal(true);
  };

  const openEditVideo = (item) => {
    setEditingItem(item);
    setVideoForm({ 
      title: item.title || '', 
      title_es: item.title_es || '', 
      description: item.description || '', 
      description_es: item.description_es || '',
      video_url: item.video_url || '',
      duration: item.duration || '',
      category: item.category || 'software',
      order: item.order || 0 
    });
    setShowVideoModal(true);
  };

  const openEditChecklist = (item) => {
    setEditingItem(item);
    setChecklistForm({ 
      title: item.title || '', 
      title_es: item.title_es || '', 
      description: item.description || '', 
      description_es: item.description_es || '',
      items: item.items || [],
      order: item.order || 0 
    });
    setShowChecklistModal(true);
  };

  const openEditNote = (item) => {
    setEditingItem(item);
    setNoteForm({ 
      resource_type: item.resource_type || 'general', 
      resource_id: item.resource_id || '', 
      note: item.note || ''
    });
    setShowNoteModal(true);
  };

  // Get notes for current section
  const getSectionNotes = () => {
    const typeMap = { manual: 'manual', videos: 'video', checklists: 'checklist' };
    const noteType = typeMap[section] || 'general';
    return adminNotes.filter(n => n.resource_type === noteType || n.resource_type === 'general');
  };

  // Render Manual Section
  const renderManual = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin?tab=resources')} className="p-2">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <FileText className="mr-2 h-6 w-6" style={{ color: COMPANY.primaryColor }} />
              {language === 'es' ? 'Manual de la Empresa' : 'Company Manual'}
            </h1>
            <p className="text-sm text-gray-500">
              {language === 'es' ? 'Mejores prácticas y procedimientos' : 'Best practices and procedures'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={editMode ? "default" : "outline"} 
            onClick={() => setEditMode(!editMode)}
            style={editMode ? { backgroundColor: COMPANY.primaryColor } : {}}
            className={editMode ? "text-white" : ""}
          >
            <Edit className="mr-2 h-4 w-4" />
            {editMode ? (language === 'es' ? 'Modo Edición ON' : 'Edit Mode ON') : (language === 'es' ? 'Editar' : 'Edit')}
          </Button>
          {editMode && (
            <Button onClick={() => { setEditingItem(null); setManualForm({ title: '', title_es: '', content: '', content_es: '', order: manualSections.length }); setShowManualModal(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              {language === 'es' ? 'Agregar Sección' : 'Add Section'}
            </Button>
          )}
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" />
            {language === 'es' ? 'Imprimir' : 'Print'}
          </Button>
        </div>
      </div>

      {manualSections.length === 0 ? (
        <Card className="p-8 text-center">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">{language === 'es' ? 'No hay secciones del manual' : 'No manual sections yet'}</p>
          <Button onClick={seedDefaultData}>
            <Plus className="mr-2 h-4 w-4" />
            {language === 'es' ? 'Cargar Contenido Predeterminado' : 'Load Default Content'}
          </Button>
        </Card>
      ) : (
        manualSections.map((item, index) => (
          <Card key={item.id} className="overflow-hidden">
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
              onClick={() => !editMode && setExpandedSection(expandedSection === item.id ? null : item.id)}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                  {index + 1}
                </div>
                <h3 className="font-semibold text-gray-800">
                  {language === 'es' ? item.title_es || item.title : item.title}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                {editMode && (
                  <>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditManual(item); }}>
                      <Edit className="h-4 w-4 text-blue-600" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deleteManualSection(item.id); }}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </>
                )}
                {expandedSection === item.id ? 
                  <ChevronDown className="h-5 w-5 text-gray-400" /> : 
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                }
              </div>
            </div>
            {expandedSection === item.id && (
              <CardContent className="border-t bg-gray-50 p-6">
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
                    {language === 'es' ? item.content_es || item.content : item.content}
                  </pre>
                </div>
              </CardContent>
            )}
          </Card>
        ))
      )}
    </div>
  );

  // Render Videos Section
  const renderVideos = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin?tab=resources')} className="p-2">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <Video className="mr-2 h-6 w-6 text-purple-600" />
              {language === 'es' ? 'Videos de Capacitación' : 'Training Videos'}
            </h1>
            <p className="text-sm text-gray-500">
              {language === 'es' ? 'Tutoriales y capacitación' : 'Tutorials and training'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={editMode ? "default" : "outline"} 
            onClick={() => setEditMode(!editMode)}
            style={editMode ? { backgroundColor: COMPANY.primaryColor } : {}}
            className={editMode ? "text-white" : ""}
          >
            <Edit className="mr-2 h-4 w-4" />
            {editMode ? (language === 'es' ? 'Modo Edición ON' : 'Edit Mode ON') : (language === 'es' ? 'Editar' : 'Edit')}
          </Button>
          {editMode && (
            <Button onClick={() => { setEditingItem(null); setVideoForm({ title: '', title_es: '', description: '', description_es: '', video_url: '', duration: '', category: 'software', order: videos.length }); setShowVideoModal(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              {language === 'es' ? 'Agregar Video' : 'Add Video'}
            </Button>
          )}
        </div>
      </div>

      {videos.length === 0 ? (
        <Card className="p-8 text-center">
          <Video className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">{language === 'es' ? 'No hay videos' : 'No videos yet'}</p>
          <Button onClick={seedDefaultData}>
            <Plus className="mr-2 h-4 w-4" />
            {language === 'es' ? 'Cargar Videos Predeterminados' : 'Load Default Videos'}
          </Button>
        </Card>
      ) : (
        <>
          {/* Software Training */}
          <div>
            <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
              <Badge className="bg-purple-100 text-purple-700 mr-2">Software</Badge>
              {language === 'es' ? 'Capacitación del Software' : 'Software Training'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.filter(v => v.category === 'software').map(video => (
                <Card key={video.id} className="hover:shadow-lg transition-shadow group relative">
                  {editMode && (
                    <div className="absolute top-2 right-2 z-10 flex gap-1">
                      <Button size="sm" variant="secondary" className="h-8 w-8 p-0" onClick={() => openEditVideo(video)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" className="h-8 w-8 p-0" onClick={() => deleteVideo(video.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center relative overflow-hidden">
                    {getYouTubeId(video.video_url) ? (
                      <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${getYouTubeId(video.video_url)}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <>
                        <PlayCircle className="h-16 w-16 text-gray-400" />
                        {!video.video_url && (
                          <span className="absolute bottom-2 left-2 text-xs text-gray-500 bg-white/80 px-2 py-1 rounded">
                            {language === 'es' ? 'Sin enlace de video' : 'No video link'}
                          </span>
                        )}
                      </>
                    )}
                    <Badge className="absolute bottom-2 right-2 bg-black/70 text-white">{video.duration || '--:--'}</Badge>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-800">
                      {language === 'es' ? video.title_es || video.title : video.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {language === 'es' ? video.description_es || video.description : video.description}
                    </p>
                    {video.video_url && !getYouTubeId(video.video_url) && (
                      <a href={video.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline mt-2 flex items-center">
                        <ExternalLink className="h-3 w-3 mr-1" />
                        {language === 'es' ? 'Ver Video' : 'Watch Video'}
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Field Training */}
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
              <Badge className="bg-green-100 text-green-700 mr-2">Field</Badge>
              {language === 'es' ? 'Capacitación de Campo' : 'Field Training'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {videos.filter(v => v.category === 'field').map(video => (
                <Card key={video.id} className="hover:shadow-lg transition-shadow group relative">
                  {editMode && (
                    <div className="absolute top-2 right-2 z-10 flex gap-1">
                      <Button size="sm" variant="secondary" className="h-8 w-8 p-0" onClick={() => openEditVideo(video)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" className="h-8 w-8 p-0" onClick={() => deleteVideo(video.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <div className="aspect-video bg-gray-200 rounded-t-lg flex items-center justify-center relative overflow-hidden">
                    {getYouTubeId(video.video_url) ? (
                      <iframe
                        className="w-full h-full"
                        src={`https://www.youtube.com/embed/${getYouTubeId(video.video_url)}`}
                        title={video.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <>
                        <PlayCircle className="h-16 w-16 text-gray-400" />
                        {!video.video_url && (
                          <span className="absolute bottom-2 left-2 text-xs text-gray-500 bg-white/80 px-2 py-1 rounded">
                            {language === 'es' ? 'Sin enlace de video' : 'No video link'}
                          </span>
                        )}
                      </>
                    )}
                    <Badge className="absolute bottom-2 right-2 bg-black/70 text-white">{video.duration || '--:--'}</Badge>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-gray-800">
                      {language === 'es' ? video.title_es || video.title : video.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {language === 'es' ? video.description_es || video.description : video.description}
                    </p>
                    {video.video_url && !getYouTubeId(video.video_url) && (
                      <a href={video.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline mt-2 flex items-center">
                        <ExternalLink className="h-3 w-3 mr-1" />
                        {language === 'es' ? 'Ver Video' : 'Watch Video'}
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
              {videos.filter(v => v.category === 'field').length === 0 && (
                <Card className="p-6 text-center col-span-full bg-gray-50">
                  <p className="text-gray-500">{language === 'es' ? 'No hay videos de campo' : 'No field training videos yet'}</p>
                </Card>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );

  // Render Checklists Section
  const renderChecklists = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin?tab=resources')} className="p-2">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <CheckSquare className="mr-2 h-6 w-6 text-green-600" />
              {language === 'es' ? 'Listas de Verificación' : 'Project Checklists'}
            </h1>
            <p className="text-sm text-gray-500">
              {language === 'es' ? 'Listas para asegurar calidad' : 'Checklists to ensure quality'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant={editMode ? "default" : "outline"} 
            onClick={() => setEditMode(!editMode)}
            style={editMode ? { backgroundColor: COMPANY.primaryColor } : {}}
            className={editMode ? "text-white" : ""}
          >
            <Edit className="mr-2 h-4 w-4" />
            {editMode ? (language === 'es' ? 'Modo Edición ON' : 'Edit Mode ON') : (language === 'es' ? 'Editar' : 'Edit')}
          </Button>
          {editMode && (
            <Button onClick={() => { setEditingItem(null); setChecklistForm({ title: '', title_es: '', description: '', description_es: '', items: [], order: checklists.length }); setShowChecklistModal(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              {language === 'es' ? 'Agregar Lista' : 'Add Checklist'}
            </Button>
          )}
        </div>
      </div>

      {checklists.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 mb-4">{language === 'es' ? 'No hay listas de verificación' : 'No checklists yet'}</p>
          <Button onClick={seedDefaultData}>
            <Plus className="mr-2 h-4 w-4" />
            {language === 'es' ? 'Cargar Listas Predeterminadas' : 'Load Default Checklists'}
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {checklists.map(checklist => (
            <Card key={checklist.id} className="hover:shadow-lg transition-shadow relative">
              {editMode && (
                <div className="absolute top-2 right-2 z-10 flex gap-1">
                  <Button size="sm" variant="secondary" className="h-8 w-8 p-0" onClick={() => openEditChecklist(checklist)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" className="h-8 w-8 p-0" onClick={() => deleteChecklist(checklist.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <CardHeader className="bg-green-50 border-b">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center">
                    <CheckSquare className="mr-2 h-5 w-5 text-green-600" />
                    {language === 'es' ? checklist.title_es || checklist.title : checklist.title}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.print()}
                  >
                    <Printer className="h-4 w-4" />
                  </Button>
                </CardTitle>
                <p className="text-sm text-gray-500">
                  {language === 'es' ? checklist.description_es || checklist.description : checklist.description}
                </p>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {(checklist.items || []).map((item, index) => (
                    <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                      <div className="w-5 h-5 border-2 border-gray-300 rounded flex-shrink-0"></div>
                      <span className="text-sm text-gray-700">
                        {language === 'es' ? item.text_es || item.text : item.text}
                      </span>
                    </div>
                  ))}
                  {(!checklist.items || checklist.items.length === 0) && (
                    <p className="text-sm text-gray-400 text-center py-4">
                      {language === 'es' ? 'No hay elementos en esta lista' : 'No items in this checklist'}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Render Admin Notes Section
  const renderAdminNotes = () => {
    const sectionNotes = getSectionNotes();
    return (
      <Card className="mt-6 border-amber-200 bg-amber-50">
        <CardHeader className="py-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span className="flex items-center text-amber-800">
              <StickyNote className="mr-2 h-4 w-4" />
              {language === 'es' ? 'Notas del Administrador' : 'Admin Notes'}
            </span>
            <Button 
              size="sm" 
              variant="outline" 
              className="border-amber-300 text-amber-700 hover:bg-amber-100"
              onClick={() => { 
                setEditingItem(null); 
                setNoteForm({ 
                  resource_type: section === 'manual' ? 'manual' : section === 'videos' ? 'video' : section === 'checklists' ? 'checklist' : 'general', 
                  resource_id: '', 
                  note: '' 
                }); 
                setShowNoteModal(true); 
              }}
            >
              <Plus className="h-3 w-3 mr-1" />
              {language === 'es' ? 'Agregar Nota' : 'Add Note'}
            </Button>
          </CardTitle>
        </CardHeader>
        {sectionNotes.length > 0 && (
          <CardContent className="pt-0">
            <div className="space-y-2">
              {sectionNotes.map(note => (
                <div key={note.id} className="flex items-start gap-2 p-2 bg-white rounded border border-amber-100">
                  <MessageSquare className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-gray-700 flex-1">{note.note}</p>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openEditNote(note)}>
                      <Edit className="h-3 w-3 text-gray-500" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => deleteNote(note.id)}>
                      <Trash2 className="h-3 w-3 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
        {sectionNotes.length === 0 && (
          <CardContent className="pt-0">
            <p className="text-sm text-amber-600 text-center py-2">
              {language === 'es' ? 'No hay notas para esta sección' : 'No notes for this section'}
            </p>
          </CardContent>
        )}
      </Card>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">{language === 'es' ? 'Cargando...' : 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {section === 'manual' && (
          <>
            {renderManual()}
            {renderAdminNotes()}
          </>
        )}
        {section === 'videos' && (
          <>
            {renderVideos()}
            {renderAdminNotes()}
          </>
        )}
        {section === 'checklists' && (
          <>
            {renderChecklists()}
            {renderAdminNotes()}
          </>
        )}
        {!section && (
          <div className="text-center py-12">
            <BookOpen className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">{language === 'es' ? 'Seleccione una sección de recursos' : 'Select a resource section from the dashboard'}</p>
            <Link to="/admin?tab=resources">
              <Button className="mt-4" style={{ backgroundColor: COMPANY.primaryColor }}>
                <ChevronLeft className="mr-2 h-4 w-4" />
                {language === 'es' ? 'Volver al Panel' : 'Back to Dashboard'}
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Manual Section Modal */}
      {showManualModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingItem ? (language === 'es' ? 'Editar Sección' : 'Edit Section') : (language === 'es' ? 'Nueva Sección' : 'New Section')}</span>
                <Button variant="ghost" size="sm" onClick={() => { setShowManualModal(false); setEditingItem(null); }}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Título (EN)' : 'Title (EN)'}</Label>
                  <Input value={manualForm.title} onChange={(e) => setManualForm({ ...manualForm, title: e.target.value })} />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Título (ES)' : 'Title (ES)'}</Label>
                  <Input value={manualForm.title_es} onChange={(e) => setManualForm({ ...manualForm, title_es: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>{language === 'es' ? 'Contenido (EN)' : 'Content (EN)'}</Label>
                <Textarea 
                  value={manualForm.content} 
                  onChange={(e) => setManualForm({ ...manualForm, content: e.target.value })} 
                  rows={10}
                  placeholder="Use markdown formatting: ## Heading, **bold**, - bullet points"
                />
              </div>
              <div>
                <Label>{language === 'es' ? 'Contenido (ES)' : 'Content (ES)'}</Label>
                <Textarea 
                  value={manualForm.content_es} 
                  onChange={(e) => setManualForm({ ...manualForm, content_es: e.target.value })} 
                  rows={10}
                  placeholder="Use markdown formatting: ## Heading, **bold**, - bullet points"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowManualModal(false); setEditingItem(null); }}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button onClick={saveManualSection} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
                  <Save className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Guardar' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingItem ? (language === 'es' ? 'Editar Video' : 'Edit Video') : (language === 'es' ? 'Nuevo Video' : 'New Video')}</span>
                <Button variant="ghost" size="sm" onClick={() => { setShowVideoModal(false); setEditingItem(null); }}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Título (EN)' : 'Title (EN)'}</Label>
                  <Input value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Título (ES)' : 'Title (ES)'}</Label>
                  <Input value={videoForm.title_es} onChange={(e) => setVideoForm({ ...videoForm, title_es: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Descripción (EN)' : 'Description (EN)'}</Label>
                  <Textarea value={videoForm.description} onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })} rows={3} />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Descripción (ES)' : 'Description (ES)'}</Label>
                  <Textarea value={videoForm.description_es} onChange={(e) => setVideoForm({ ...videoForm, description_es: e.target.value })} rows={3} />
                </div>
              </div>
              <div>
                <Label>{language === 'es' ? 'URL del Video (YouTube o enlace externo)' : 'Video URL (YouTube or external link)'}</Label>
                <Input 
                  value={videoForm.video_url} 
                  onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value })} 
                  placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Duración' : 'Duration'}</Label>
                  <Input 
                    value={videoForm.duration} 
                    onChange={(e) => setVideoForm({ ...videoForm, duration: e.target.value })} 
                    placeholder="5:30"
                  />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Categoría' : 'Category'}</Label>
                  <Select value={videoForm.category} onValueChange={(v) => setVideoForm({ ...videoForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="software">{language === 'es' ? 'Software' : 'Software Training'}</SelectItem>
                      <SelectItem value="field">{language === 'es' ? 'Campo' : 'Field Training'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowVideoModal(false); setEditingItem(null); }}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button onClick={saveVideo} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
                  <Save className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Guardar' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Checklist Modal */}
      {showChecklistModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingItem ? (language === 'es' ? 'Editar Lista' : 'Edit Checklist') : (language === 'es' ? 'Nueva Lista' : 'New Checklist')}</span>
                <Button variant="ghost" size="sm" onClick={() => { setShowChecklistModal(false); setEditingItem(null); }}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Título (EN)' : 'Title (EN)'}</Label>
                  <Input value={checklistForm.title} onChange={(e) => setChecklistForm({ ...checklistForm, title: e.target.value })} />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Título (ES)' : 'Title (ES)'}</Label>
                  <Input value={checklistForm.title_es} onChange={(e) => setChecklistForm({ ...checklistForm, title_es: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{language === 'es' ? 'Descripción (EN)' : 'Description (EN)'}</Label>
                  <Input value={checklistForm.description} onChange={(e) => setChecklistForm({ ...checklistForm, description: e.target.value })} />
                </div>
                <div>
                  <Label>{language === 'es' ? 'Descripción (ES)' : 'Description (ES)'}</Label>
                  <Input value={checklistForm.description_es} onChange={(e) => setChecklistForm({ ...checklistForm, description_es: e.target.value })} />
                </div>
              </div>
              
              {/* Checklist Items */}
              <div>
                <Label className="mb-2 block">{language === 'es' ? 'Elementos de la Lista' : 'Checklist Items'}</Label>
                <div className="space-y-2 mb-3">
                  {checklistForm.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                      <CheckSquare className="h-4 w-4 text-green-600 flex-shrink-0" />
                      <div className="flex-1 grid grid-cols-2 gap-2 text-sm">
                        <span>{item.text}</span>
                        <span className="text-gray-500">{item.text_es}</span>
                      </div>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeChecklistItem(index)}>
                        <X className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input 
                    placeholder={language === 'es' ? 'Nuevo elemento (EN)' : 'New item (EN)'} 
                    value={newChecklistItem.text}
                    onChange={(e) => setNewChecklistItem({ ...newChecklistItem, text: e.target.value })}
                    className="flex-1"
                  />
                  <Input 
                    placeholder={language === 'es' ? 'Traducción (ES)' : 'Translation (ES)'} 
                    value={newChecklistItem.text_es}
                    onChange={(e) => setNewChecklistItem({ ...newChecklistItem, text_es: e.target.value })}
                    className="flex-1"
                  />
                  <Button size="sm" onClick={addChecklistItem}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowChecklistModal(false); setEditingItem(null); }}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button onClick={saveChecklist} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
                  <Save className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Guardar' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Note Modal */}
      {showNoteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editingItem ? (language === 'es' ? 'Editar Nota' : 'Edit Note') : (language === 'es' ? 'Nueva Nota' : 'New Note')}</span>
                <Button variant="ghost" size="sm" onClick={() => { setShowNoteModal(false); setEditingItem(null); }}>
                  <X className="h-4 w-4" />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>{language === 'es' ? 'Tipo de Recurso' : 'Resource Type'}</Label>
                <Select value={noteForm.resource_type} onValueChange={(v) => setNoteForm({ ...noteForm, resource_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">{language === 'es' ? 'General' : 'General'}</SelectItem>
                    <SelectItem value="manual">{language === 'es' ? 'Manual' : 'Manual'}</SelectItem>
                    <SelectItem value="video">{language === 'es' ? 'Videos' : 'Videos'}</SelectItem>
                    <SelectItem value="checklist">{language === 'es' ? 'Listas' : 'Checklists'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{language === 'es' ? 'Nota' : 'Note'}</Label>
                <Textarea 
                  value={noteForm.note} 
                  onChange={(e) => setNoteForm({ ...noteForm, note: e.target.value })} 
                  rows={4}
                  placeholder={language === 'es' ? 'Escriba su nota aquí...' : 'Write your note here...'}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowNoteModal(false); setEditingItem(null); }}>
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button onClick={saveNote} style={{ backgroundColor: COMPANY.primaryColor }} className="text-white">
                  <Save className="mr-2 h-4 w-4" />
                  {language === 'es' ? 'Guardar' : 'Save'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
